import { PhoneAlt } from '@cat-home-experts/mortar-iconography-web';
import { Button } from '@checkatrade/web-ui';
import useIsMobileScreen from '@hooks/use-is-mobile-screen';
import { breakpointMedium, breakpointSmall } from 'lib/design-system';
import { useState, type MouseEvent, type MouseEventHandler } from 'react';
import styled from 'styled-components';

type RevealPhoneButtonProps = {
  phone: {
    number?: string | null | undefined;
    label?: string | null | undefined;
  };
  showIcon?: boolean;
  onRevealPhone?: (event: MouseEvent<HTMLButtonElement>) => void;
  onCallClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  prioritiseCalls?: boolean;
};

export function RevealPhoneButton({
  phone,
  showIcon,
  onRevealPhone,
  onCallClick,
  prioritiseCalls,
}: RevealPhoneButtonProps) {
  const [isPhoneVisible, setIsPhoneVisible] = useState(false);
  const isMobile = useIsMobileScreen();

  const handleClick: MouseEventHandler<HTMLButtonElement> = event => {
    if (isMobile || isPhoneVisible) {
      onCallClick?.(event);
    } else {
      event.preventDefault();
      event.stopPropagation();
      setIsPhoneVisible(true);
      onRevealPhone?.(event);
    }
  };

  return (
    <Button
      variant={!prioritiseCalls ? 'outline' : 'primary'}
      className="text-xl"
      startContent={showIcon ? <PhoneAlt size={20} color="" /> : null}
      href={`tel:${phone.number}`}
      onClick={handleClick}
      data-guid="CallTrade"
    >
      <MobileOnly>Call</MobileOnly>
      <TabletOnly>
        <RevealLabel show={!isPhoneVisible}>Show phone number</RevealLabel>
        <PhoneLabel show={isPhoneVisible}>{phone.label}</PhoneLabel>
      </TabletOnly>
    </Button>
  );
}

const MobileOnly = styled.span<StyledSpan>`
  @media (min-width: ${breakpointMedium.start}) {
    display: none;
  }
`;

const TabletOnly = styled.span<StyledSpan>`
  @media (max-width: ${breakpointSmall.end}) {
    display: none;
  }

  display: grid;
  grid-template: 1fr / 1fr;
  justify-items: center;
  align-items: center;
`;

const RevealLabel = styled.span<StyledSpan & { show: boolean }>`
  transition:
    transform 0.275s cubic-bezier(0.82, -0.04, 0.27, 0.85),
    opacity 0.275s cubic-bezier(0.82, -0.04, 0.27, 0.85),
    max-width 0.275s cubic-bezier(0.82, -0.04, 0.27, 0.85);
  user-select: none;
  display: inline-block;
  grid-area: 1 / 1 / 2 / 2;
  white-space: nowrap;
  overflow: hidden;

  transform: ${({ show }) => (show ? 'translateY(0)' : 'translateY(-10px)')};
  opacity: ${({ show }) => (show ? 1 : 0)};
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const PhoneLabel = styled.span<StyledSpan & { show: boolean }>`
  transition:
    transform 0.275s cubic-bezier(0.82, -0.04, 0.27, 0.85),
    opacity 0.275s cubic-bezier(0.82, -0.04, 0.27, 0.85);
  grid-area: 1 / 1 / 2 / 2;
  display: inline-block;

  transform: ${({ show }) => (show ? 'translateY(0)' : 'translateY(10px)')};
  opacity: ${({ show }) => (show ? 1 : 0)};
`;
